import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { FiSave, FiX, FiPlus, FiGrid, FiEdit, FiTrash } from 'react-icons/fi';
import { PieChart, Pie, Cell, ResponsiveContainer, Sector } from 'recharts';
import axios from 'axios';
import Swal from 'sweetalert2';
import { GridLayout, useContainerWidth } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import './CustomDashboard.css';
import Alert from '../common/Alerts/Alert';
import API_ENDPOINTS from '../config/apiEndpoints';
import SubHeader from '../components/common/SubHeader/SubHeader';

const CustomDashboard = ({ onClose }) => {
  const { width, containerRef } = useContainerWidth();
  const [selectedDashboard, setSelectedDashboard] = useState('');
  const [selectedSection, setSelectedSection] = useState('');
  const [dashboardList, setDashboardList] = useState([]);
  const [sectionList, setSectionList] = useState([]);
  const [availableReports, setAvailableReports] = useState([]);
  const [libraryReports, setLibraryReports] = useState([]); // Widgets available for current dashboard
  const [sectionReports, setSectionReports] = useState([]); // Specifically for filtered dropdown
  const [canvasWidgets, setCanvasWidgets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sidebarLoading, setSidebarLoading] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editDashName, setEditDashName] = useState('');
  const [newDashName, setNewDashName] = useState('');
  const [showCloseAlert, setShowCloseAlert] = useState(false);
  const lastFetchedDash = React.useRef('');
  const lastDropTime = React.useRef(0);

  const TOKEN = API_ENDPOINTS.TOKE.UNIVERSAL_TOKEN;
  const API_BASE_SERVICES = (process.env.REACT_APP_SERVICES_AZURE_BASEURL || '').replace(/\/$/, '');
  const Get_Custome_dashBoard_List = `${API_BASE_SERVICES}${API_ENDPOINTS.DASHBOARD.GET_CUSTOME_DASHBOARD_LIST}`;
  const Get_Custome_SectionData_List = `${API_BASE_SERVICES}${API_ENDPOINTS.DASHBOARD.GET_SECTION_DATA}`;
  const Load_reports = `${API_BASE_SERVICES}${API_ENDPOINTS.DASHBOARD.LOAD_REPORTS}`;
  const Load_reports_By_Sections = `${API_BASE_SERVICES}${API_ENDPOINTS.DASHBOARD.LOAD_REPORTS_BY_SECTIONS}`;
  const Get_saveTiles = `${API_BASE_SERVICES}${API_ENDPOINTS.DASHBOARD.GET_SAVE_TILES}`;
  const Save_Tiles = `${API_BASE_SERVICES}${API_ENDPOINTS.DASHBOARD.SAVE_TILES}`;
  const Delete_DashBoard = `${API_BASE_SERVICES}${API_ENDPOINTS.DASHBOARD.DELETE_DASHBOARD}`;
  const Edit_DashBoard = `${API_BASE_SERVICES}${API_ENDPOINTS.DASHBOARD.EDIT_DASHBOARD}`;
  const Total_DashBoard_Count = `${API_BASE_SERVICES}${API_ENDPOINTS.DASHBOARD.TOTOL_DashBoard}`;
  const Save_DashBoard = `${API_BASE_SERVICES}${API_ENDPOINTS.DASHBOARD.SAVE_CUSTOME_DASHBOARD}`;

  const COLORS = [
    '#3b82f6', '#6366f1', '#4f46e5', '#10b981', '#f97316',
    '#64748b', '#d946ef', '#06b6d4', '#ef4444', '#99f6e4'
  ];

  // Robust data extractor for Kit19 APIs
  const parseResponseData = (res) => {
    if (!res || !res.data) return [];
    let data = res.data.d || res.data;
    let details = data.Details || data;

    if (typeof details === 'string') {
      try {
        details = JSON.parse(details);
      } catch (e) {
        console.error('Parse error during robust extraction:', e);
        return [];
      }
    }

    // Some APIs return directly an array, some nest in Table / List / Table1
    if (Array.isArray(details)) return details;
    if (details && typeof details === 'object') {
      return details.Table || details.List || details.Table1 || details.tiles || [];
    }
    return [];
  };
  const dummyListData = [
    { name: 'Mr. Bansal', score: '0.05', id: '137749' },
    { name: 'Rohit', score: '0.05', id: '117758' },
    { name: 'Arun', score: '0.05', id: '122715' },
    { name: 'Rina', score: '0.05', id: '118583' },
  ];
  const dummyPieData = [
    { name: 'Australia', value: 42.2 },
    { name: 'Codeffective', value: 10.2 },
    { name: 'Online enquiry', value: 10.2 },
    { name: 'Super Automation', value: 8.2 },
    { name: 'CAR DEKHO', value: 7.2 },
    { name: 'free whatsapp', value: 6.6 },
    { name: 'Walkins', value: 4.1 },
    { name: 'UAE', value: 3.8 },
    { name: 'Concave', value: 3.1 },
    { name: 'salesman', value: 2.8 },
  ];

  useEffect(() => {
    fetchDashboards();
    fetchSectionData();
    fetchAvailableReports();
  }, []);

  // 3️⃣ When User Selects Dashboard -> Call GetSavedTiles & Load Library
  useEffect(() => {
    if (selectedDashboard && availableReports.length > 0) {
      if (lastFetchedDash.current === selectedDashboard) return;
      fetchSavedLayout(selectedDashboard);
      fetchDashboardLibrary();
      lastFetchedDash.current = selectedDashboard;
    }
  }, [selectedDashboard, availableReports]);

  // 6️⃣ Load Reports by Section - ONLY for the sidebar drag-panel
  useEffect(() => {
    if (selectedDashboard && selectedSection) {
      fetchSavedReportsBySection();
    }
  }, [selectedDashboard, selectedSection]);

  const fetchDashboards = async () => {
    try {
      const ustrUserId = localStorage.getItem('ustrUserId') || localStorage.getItem('userId') || localStorage.getItem('UserId') || '';
      const payload = { Status: "", Message: "", Token: String(TOKEN), Details: { UserId: ustrUserId, ManageDash: 1 } };
      const response = await axios.post(Get_Custome_dashBoard_List, payload);
      if (response.data && response.data.Details) {
        setDashboardList(Array.isArray(response.data.Details) ? response.data.Details : []);
      }
    } catch (error) { console.error('Error fetching dashboard list:', error); }
  };

  const fetchSectionData = async () => {
    try {
      const payload = { Status: "", Message: "", Token: String(TOKEN), Details: {} };
      const response = await axios.post(Get_Custome_SectionData_List, payload);
      if (response.data && response.data.Details) {
        setSectionList(Array.isArray(response.data.Details) ? response.data.Details : []);
      }
    } catch (error) { console.error('Error fetching section data:', error); }
  };

  const fetchAvailableReports = async () => {
    setLoading(true);
    try {
      const payload = { Status: "", Message: "", Token: String(TOKEN), Details: {} };
      const response = await axios.post(Load_reports, payload);
      setAvailableReports(parseResponseData(response));
    } catch (error) {
      console.error('Error loading reports:', error);
    } finally { setLoading(false); }
  };

  const fetchSavedReportsBySection = async () => {
    if (!selectedSection || !selectedDashboard) {
      setSectionReports([]);
      return;
    }
    setSidebarLoading(true);
    try {
      const ustrUserId = localStorage.getItem('ustrUserId') || localStorage.getItem('userId') || localStorage.getItem('UserId') || '';
      const payload = {
        Status: "",
        Message: "",
        Token: String(TOKEN),
        Details: { SectionID: String(selectedSection), DashboardId: String(selectedDashboard), UserId: String(ustrUserId) }
      };
      const response = await axios.post(Load_reports_By_Sections, payload);
      const reports = parseResponseData(response);
      setSectionReports(reports);
    } catch (error) {
      console.error('Error loading section reports:', error);
    } finally {
      setSidebarLoading(false);
    }
  };

  const getTypeFromName = (report) => {
    const sectionName = (report.SectionName || report.ReportType || '').toLowerCase();
    const name = (report.ReportName || report.Name || '').toLowerCase();
    const type = (report.ReportType || report.Type || report.ChartType || '').toLowerCase();

    // Explicit overrides for known list types that might be confused with charts
    if (name.includes('sourcewise lead') && !name.includes('distribution')) return 'list';
    if (name.includes('leads & followup')) return 'list';

    if (type.includes('chart') || sectionName.includes('chart') || name.includes('distribution')) return 'chart';
    if (type.includes('list') || sectionName.includes('list') || name.includes('followup') || name.includes('lead') || name.includes('pipeline') || name.includes('top 10')) return 'list';
    return 'counter';
  };

  const fetchDashboardLibrary = async () => {
    if (!selectedDashboard) return;
    try {
      const ustrUserId = localStorage.getItem('ustrUserId') || localStorage.getItem('userId') || localStorage.getItem('UserId') || '';
      const payload = {
        Status: "",
        Message: "",
        Token: String(TOKEN),
        Details: {
          SectionID: "1",
          DashboardId: String(selectedDashboard),
          UserId: String(ustrUserId)
        }
      };
      console.log('Fetching Dashboard Widget Library (SectionID: 1):', payload);
      const response = await axios.post(Load_reports_By_Sections, payload);
      setLibraryReports(parseResponseData(response));
    } catch (error) {
      console.error('Error loading dashboard library:', error);
    }
  };

  const fetchSavedLayout = async (dashId) => {
    if (!dashId) return;
    setLoading(true);
    try {
      lastFetchedDash.current = dashId;
      const ustrUserId = localStorage.getItem('ustrUserId') || localStorage.getItem('userId') || localStorage.getItem('UserId') || '';

      const payload = {
        Status: "", Message: "", Token: String(TOKEN),
        Details: { UserId: parseInt(ustrUserId, 10), CustomDashboardId: parseInt(dashId, 10), IsFromManage: 1 }
      };

      const [saveRes, libRes] = await Promise.all([
        axios.post(Get_saveTiles, payload),
        axios.post(Load_reports_By_Sections, {
          Status: "", Message: "", Token: String(TOKEN),
          Details: { SectionID: "0", DashboardId: dashId, UserId: ustrUserId }
        })
      ]);

      let savedItems = saveRes.data?.Details?.tiles || saveRes.data?.Details || [];
      // If backend returns a non-array but has items, wrap it
      if (!Array.isArray(savedItems)) savedItems = [savedItems];

      // Merge and filter valid widgets, generating unique IDs
      const validWidgets = savedItems
        .filter(tile => tile && (tile.ReportName || tile.ReportType))
        .map((tile, index) => {
          const rawType = (tile.ReportType || tile.Type || '').toLowerCase();
          const normalizedType = rawType.includes('chart') ? 'chart' :
            rawType.includes('list') ? 'list' : 'counter';

          return {
            ...tile,
            ReportType: normalizedType,
            instanceId: `saved-${tile.ReportId || tile.Id || index}`,
            PositionX: Number(tile.PositionX || tile.X || 0),
            PositionY: Number(tile.PositionY || tile.Y || 0),
            Width: Number(tile.Width || (normalizedType === 'counter' ? 4 : 6)),
            Height: Number(tile.Height || (normalizedType === 'counter' ? 1 : 5))
          };
        });

      setCanvasWidgets(validWidgets);
    } catch (error) {
      console.error('Error fetching layout:', error);
    } finally {
      setLoading(false);
    }
  };

  const syncBulkLayout = async (widgetsToSync) => {
    if (!selectedDashboard) {
      console.warn('Sync aborted: No dashboard selected.');
      return false;
    }
    try {
      const ustrUserId = localStorage.getItem('ustrUserId') || localStorage.getItem('userId') || localStorage.getItem('UserId') || '';
      const listToSync = widgetsToSync || canvasWidgets;

      console.log('Starting sync for weights:', listToSync);

      // Coordinate uniqueness guard
      const occupied = new Set();
      const payloadTiles = [];

      listToSync.forEach(widget => {
        // Requirement: Allow all widgets on canvas to be saved (Removing the IsSaveTiles exclusion)

        const rawType = (widget.ReportType || '').toLowerCase();
        let mappedType = 'Counter';
        let sectionID = 1;

        // Requirement 2: Strict SectionID Mapping
        if (rawType.includes('chart')) {
          mappedType = 'Chart';
          sectionID = 2;
        } else if (rawType.includes('list')) {
          mappedType = 'List View';
          sectionID = 3;
        }

        // Coordinate deduplication
        let x = Number(widget.PositionX || 0);
        let y = Number(widget.PositionY || 0);
        while (occupied.has(`${x},${y}`)) {
          y++; // Push down until free
        }
        occupied.add(`${x},${y}`);

        payloadTiles.push({
          ReportName: widget.ReportName || "",
          ReportType: mappedType,
          SectionID: sectionID,
          PositionX: x,
          PositionY: y,
          Width: Number(widget.Width || (mappedType === 'Counter' ? 4 : 4)),
          Height: Number(widget.Height || (mappedType === 'Counter' ? 1 : 4)),
          // Requirement 4: Both IDs inside each tile to be safe
          DashboardId: Number(selectedDashboard),
          CustomDashboardId: Number(selectedDashboard)
        });
      });

      const payload = {
        Token: String(TOKEN),
        Details: {
          DashboardId: Number(selectedDashboard),
          UserId: Number(ustrUserId),
          tiles: payloadTiles
        }
      };

      console.log("Final tiles for payload:", payloadTiles);

      if (payloadTiles.length === 0) {
        console.warn('Sync aborted: No saveable tiles found.');
        return false;
      }

      console.log('Sending Save_Tiles request...');
      await axios.post(Save_Tiles, payload);
      return true;
    } catch (error) {
      console.error('Error syncing layout:', error);
      throw error;
    }
  };

  const onDragStart = (e, report) => {
    console.log("Drag Start: setting report data", report);
    e.dataTransfer.setData('report', JSON.stringify(report));
    e.dataTransfer.effectAllowed = "copy";
  };

  const onDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  };

  // Manual drop handler with grid coordinate calculation
  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent bubbling to parent containers

    // Temporal Guard: Prevent double/triple drop events firing in same tick
    const now = Date.now();
    if (now - lastDropTime.current < 200) {
      console.log("Blocking duplicate drop event (temporal guard)");
      return;
    }
    lastDropTime.current = now;

    console.log("Raw Drop Event captured at:", e.clientX, e.clientY);

    if (!selectedDashboard) {
      Swal.fire({ title: 'Info', text: 'Please select a dashboard before adding widgets.', icon: 'info' });
      return;
    }

    const reportData = e.dataTransfer.getData('report');
    console.log("Retrieved reportData from drop:", reportData);

    if (reportData) {
      try {
        const report = JSON.parse(reportData);
        const reportType = getTypeFromName(report);

        if (!containerRef.current) {
          console.error("Critical: containerRef.current is missing during drop!");
          return;
        }

        // Calculate drop position relative to container
        const rect = containerRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        // Translate to 12-column grid
        const colWidth = rect.width / 12;
        const gridX = Math.min(Math.max(Math.floor(x / colWidth), 0), 11);
        const gridY = Math.max(Math.floor(y / 90), 0); // 90 is rowHeight

        console.log(`Calculated Grid Position: X=${gridX}, Y=${gridY} (Pixel Offset: ${x}, ${y})`);

        const newWidget = {
          ...report,
          ReportName: report.ReportName || report.Report_Name || report.Name || report.reportname || report.name || report.CustomDashboard || 'New Widget',
          ReportType: reportType,
          PositionX: gridX,
          PositionY: gridY,
          Width: reportType.includes('counter') ? 4 : 4,
          Height: reportType.includes('counter') ? 1 : 4,
          instanceId: `saved-${report.ReportId || report.Id || Date.now()}`,
          PlaceholderValue: Math.floor(Math.random() * 90) + 10
        };

        console.log("Adding new widget to canvas:", newWidget);
        setCanvasWidgets(prev => [...prev, newWidget]);
      } catch (err) {
        console.error("Error processing drop data:", err, reportData);
      }
    } else {
      console.warn("Drop occurred but no 'report' data was found in dataTransfer.");
    }
  };

  const onLayoutChange = (layout) => {
    setCanvasWidgets(prev =>
      prev.map(w => {
        const l = layout.find(item => item.i === w.instanceId);
        if (l) {
          return { ...w, PositionX: l.x, PositionY: l.y, Width: l.w, Height: l.h };
        }
        return w;
      })
    );
  };

  const handleSaveLayout = async () => {
    if (!selectedDashboard) {
      Swal.fire({ title: 'Info', text: 'Please select a dashboard to save.', icon: 'info' });
      return;
    }

    setLoading(true);
    try {
      const totalWidgets = canvasWidgets.length;
      const didSync = await syncBulkLayout();

      if (didSync) {
        Swal.fire({ title: 'Success', text: 'Dashboard saved successfully', icon: 'success' });
        fetchSavedLayout(selectedDashboard);
      } else {
        if (totalWidgets > 0) {
          Swal.fire({ title: 'Error', text: 'Failed to sync layout components. Please try again.', icon: 'error' });
        } else {
          Swal.fire({
            title: 'Nothing to Save',
            text: 'There are no widgets on your canvas. Drag some widgets here first!',
            icon: 'warning'
          });
        }
      }
    } catch (error) {
      console.error('Error saving layout:', error);
      Swal.fire({ title: 'Error', text: 'Failed to save layout.', icon: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDashboard = async () => {
    if (!selectedDashboard) {
      alert("Please select a dashboard to delete.");
      return;
    }

    if (!window.confirm("Are you sure you want to permanently delete this dashboard?")) {
      return;
    }

    setLoading(true);
    try {
      const ustrUserId = localStorage.getItem('ustrUserId') || localStorage.getItem('userId') || localStorage.getItem('UserId') || '';
      const payload = {
        Status: "",
        Message: "",
        Token: String(TOKEN),
        Details: {
          UserId: parseInt(ustrUserId, 10),
          CustomDashboardId: parseInt(selectedDashboard, 10)
        }
      };

      const response = await axios.post(Delete_DashBoard, payload);

      // Check for specific restriction message in Details
      const detailsMsg = response.data.Details;
      if (typeof detailsMsg === 'string' && detailsMsg.toLowerCase().includes('cannot delete')) {
        Swal.fire({
          title: 'Error',
          text: detailsMsg,
          icon: 'error',
          confirmButtonText: 'OK',
          confirmButtonColor: '#ef4444'
        });
        return;
      }

      Swal.fire({
        title: 'Success',
        text: 'Dashboard deleted successfully.',
        icon: 'success',
        confirmButtonColor: '#3b82f6'
      });

      // Cleanup UI
      setSelectedDashboard('');
      setCanvasWidgets([]);
      setLibraryReports([]);
      setSectionReports([]);

      // Refresh list
      fetchDashboards();
    } catch (error) {
      console.error('Error deleting dashboard:', error);
      Swal.fire({
        title: 'Error',
        text: 'Failed to delete dashboard.',
        icon: 'error',
        confirmButtonColor: '#ef4444'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenEditModal = () => {
    if (!selectedDashboard) {
      Swal.fire({ title: 'Info', text: 'Please select a dashboard to edit.', icon: 'info' });
      return;
    }
    setEditDashName(selectedDashboardName);
    setIsEditModalOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editDashName.trim()) return;

    setLoading(true);
    try {
      const ustrUserId = localStorage.getItem('ustrUserId') || localStorage.getItem('userId') || localStorage.getItem('UserId') || '';
      const payload = {
        Status: "",
        Message: "",
        Token: String(TOKEN),
        Details: {
          UserId: String(ustrUserId),
          customDashboardData: {
            Id: String(selectedDashboard),
            CustomDashboard: editDashName
          }
        }
      };

      const response = await axios.post(Edit_DashBoard, payload);

      // Success check: status 0 usually means success in this API pattern
      if (response.data && (response.data.Status === "0" || response.data.Status === 0 || response.data.Status === "")) {
        Swal.fire({ title: 'Success', text: 'Dashboard renamed successfully!', icon: 'success', confirmButtonColor: '#3b82f6' });
        setIsEditModalOpen(false);
        fetchDashboards();
      } else {
        Swal.fire({ title: 'Error', text: response.data?.Message || 'Failed to rename dashboard.', icon: 'error', confirmButtonColor: '#ef4444' });
      }
    } catch (error) {
      console.error('Error editing dashboard:', error);
      Swal.fire({ title: 'Error', text: 'Failed to rename dashboard.', icon: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handlePlusClick = async () => {
    try {
      setLoading(true);
      const ustrUserId = localStorage.getItem('ustrUserId') || localStorage.getItem('userId') || localStorage.getItem('UserId') || '';
      const payload = { Status: "", Message: "", Token: String(TOKEN), Details: { UserId: ustrUserId } };
      const response = await axios.post(Total_DashBoard_Count, payload);

      let count = 0;
      if (response.data && response.data.Details) {
        try {
          // Response.Details is often a stringified JSON containing success and TotalCustomDashboard
          const details = JSON.parse(response.data.Details);
          count = details.TotalCustomDashboard || 0;
        } catch (e) {
          console.error("Error parsing TotalCustomDashboard details:", e);
        }
      }

      if (count >= 10) {
        await Swal.fire({
          title: 'Limit Reached',
          text: `You have already created ${count} custom dashboards, which is the maximum limit.`,
          icon: 'warning',
          confirmButtonText: 'OK',
          confirmButtonColor: '#3b82f6'
        });
        return;
      }

      await Swal.fire({
        title: 'Dashboard Status',
        text: `You currently have ${count} custom dashboard(s).`,
        icon: 'info',
        confirmButtonText: 'Create New',
        confirmButtonColor: '#3b82f6'
      });

      setNewDashName('');
      setIsCreateModalOpen(true);
    } catch (error) {
      console.error('Error fetching dashboard count:', error);
      Swal.fire({ title: 'Error', text: 'Failed to fetch dashboard limit.', icon: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    if (!newDashName.trim()) return;

    setLoading(true);
    try {
      const ustrUserId = localStorage.getItem('ustrUserId') || localStorage.getItem('userId') || localStorage.getItem('UserId') || '';
      const payload = {
        Status: "",
        Message: "",
        Token: String(TOKEN),
        Details: {
          UserId: String(ustrUserId),
          CustomDashboard: newDashName,
          IsActive: 1,
          IsDelete: 0
        }
      };

      const response = await axios.post(Save_DashBoard, payload);

      // The user wants a specific alert message from the response or just generic success
      Swal.fire({
        title: 'Success',
        text: 'Custom Dashboard Created successfully!',
        icon: 'success',
        confirmButtonColor: '#10b981'
      });

      setIsCreateModalOpen(false);
      fetchDashboards();
    } catch (error) {
      console.error('Error creating dashboard:', error);
      Swal.fire({ title: 'Error', text: 'Failed to create dashboard.', icon: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const removeWidget = (instanceId) => {
    setCanvasWidgets(prev => prev.filter(w => w.instanceId !== instanceId));
  };

  const handleCloseClick = () => {
    setShowCloseAlert(true);
  };

  const handleConfirmClose = () => {
    setShowCloseAlert(false);
    onClose();
  };

  const handleCancelClose = () => {
    setShowCloseAlert(false);
  };

  const selectedDashboardName = dashboardList.find(d => (d.Id || d.DashboardId) == selectedDashboard)?.CustomDashboard || 'Dashboard';
  const categorizedReports = {
    'Counter': availableReports.filter(r => (r.ReportType || r.Type) === 'Counter'),
    'List View': availableReports.filter(r => (r.ReportType || r.Type) === 'List'),
    'Chart': availableReports.filter(r => (r.ReportType || r.Type) === 'Chart')
  };
  // Robust sidebar data resolution: 
  // 1. Current section reports
  // 2. Dashboard library (Section ID 1/0)
  // 3. Fallback to all available reports if nothing else found
  let reportsToDisplay = (selectedSection && selectedSection !== "0") ? sectionReports : libraryReports;
  if (!reportsToDisplay || reportsToDisplay.length === 0) {
    reportsToDisplay = availableReports;
  }

  // Requirement: Hide items already on canvas to prevent accidental multiple drops of the same widget
  const filteredReports = reportsToDisplay.filter(r => {
    const rName = (r.ReportName || r.Name || r.CustomDashboard || '').trim().toLowerCase();
    const isAlreadyOnCanvas = canvasWidgets.some(cw => (cw.ReportName || cw.Name || '').trim().toLowerCase() === rName);
    return !isAlreadyOnCanvas;
  });

  const categorizedLibrary = {
    'Counter': filteredReports.filter(r => getTypeFromName(r) === 'counter'),
    'List View': filteredReports.filter(r => getTypeFromName(r) === 'list'),
    'Chart': filteredReports.filter(r => getTypeFromName(r) === 'chart'),
  };

  return (
    <div className="custom-dashboard-overlay">
      <div className="custom-dashboard-container">
        {createPortal(
          <div className="header-right" style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
            <button
              className="btn-save"
              onClick={handleSaveLayout}
              disabled={loading}
              style={{
                background: '#84cc16',
                color: '#fff',
                border: 'none',
                padding: '8px 20px',
                borderRadius: '6px',
                fontWeight: 600,
                fontSize: '0.85rem',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              <FiSave size={16} /> {loading ? 'Saving...' : 'Save'}
            </button>
            <button
              className="btn-cancel-dashboard"
              onClick={handleCloseClick}
                style={{
                  background: '#fff',
                  color: '#ef4444',
                  border: '1px solid #ef4444',
                  padding: '8px 20px',
                  borderRadius: '6px',
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  marginRight: '8px',
                  marginLeft: '8px'
                }}
            >
              Close
            </button>
          </div>,
          document.getElementById('custom-dashboard-actions-portal') || document.body
        )}

        <div className="custom-dashboard-main">
          <div className="custom-dashboard-preview">
            <div className="preview-canvas-premium" ref={containerRef} onDragOver={onDragOver} onDrop={onDrop}>
              <GridLayout
                className="layout"
                layout={canvasWidgets.map(w => ({
                  i: w.instanceId,
                  x: Number(w.PositionX || 0),
                  y: Number(w.PositionY || 0),
                  w: Number(w.Width || 4),
                  h: Number(w.Height || (w.ReportType.toLowerCase().includes('counter') ? 1 : 4))
                }))}
                cols={12}
                rowHeight={90}
                width={width || 1200}
                onLayoutChange={onLayoutChange}
                draggableHandle=".widget-header-premium"
                margin={[20, 20]}
                containerPadding={[20, 20]}
                compactType="vertical"
              >
                {canvasWidgets.map((widget) => (
                  <div
                    key={widget.instanceId}
                    className={`canvas-widget-premium ${(widget.ReportType || widget.Type || 'list').toLowerCase()}`}
                  >
                    <div className="widget-header-premium">
                      <span>{widget.ReportName}</span>
                      <button className="btn-remove-mini" onClick={() => removeWidget(widget.instanceId)}><FiX /></button>
                    </div>
                    <div className="widget-content-premium">
                      <WidgetContent widget={widget} />
                    </div>
                  </div>
                ))}
              </GridLayout>

              {canvasWidgets.length === 0 && (
                <div className="canvas-placeholder">
                  <FiGrid size={48} />
                  <p>Drag widgets here to build your dashboard</p>
                </div>
              )}
            </div>
          </div>

          <aside className="custom-dashboard-sidebar">
            <div className="sidebar-top-nav">
              <span className="current-dash-name">{selectedDashboardName}</span>
              <div className="dash-actions">
                <button className="icon-btn danger" onClick={handleDeleteDashboard} disabled={!selectedDashboard} title="Delete Dashboard">
                  <FiTrash />
                </button>
                <button className="icon-btn primary" onClick={handleOpenEditModal} disabled={!selectedDashboard} title="Edit Dashboard">
                  <FiEdit />
                </button>
                <button className="icon-btn info" onClick={handlePlusClick} title="Create New Dashboard">
                  <FiPlus />
                </button>
              </div>
            </div>

            <div className="sidebar-section">
              <select className="dashboard-select" value={selectedDashboard} onChange={(e) => setSelectedDashboard(e.target.value)}>
                <option value="">Nothing selected</option>
                {dashboardList.map(dash => (
                  <option key={dash.Id || dash.DashboardId} value={dash.Id || dash.DashboardId}>{dash.CustomDashboard}</option>
                ))}
              </select>
            </div>

            <div className="sidebar-section">
              <select className="dashboard-select" value={selectedSection} onChange={(e) => setSelectedSection(e.target.value)}>
                <option value="">Nothing selected</option>
                {sectionList.map(sec => (
                  <option key={sec.Id || sec.SectionId} value={sec.Id || sec.SectionId}>{sec.Name}</option>
                ))}
              </select>
            </div>

            <div className="sidebar-widgets">
              {(loading || sidebarLoading) ? (
                <div className="loading-sidebar">
                  <div className="widget-loading-pulse">Updating widget list...</div>
                </div>
              ) : (
                <>
                  {/* Optimized Unified Library rendering */}
                  {Object.entries(categorizedLibrary).map(([type, reports]) => reports.length > 0 && (
                    <div key={type} className="widget-category">
                      <span className="category-label">{type}</span>
                      {reports.map((report, idx) => (
                        <div key={report.ReportId || `${type}-${idx}`} className="widget-item draggable" draggable onDragStart={(e) => onDragStart(e, report)}>
                          <span className="drag-handle"><div className="dots-icon">::</div></span>
                          <span className="item-name">{report.ReportName || report.Name || report.CustomDashboard}</span>
                        </div>
                      ))}
                    </div>
                  ))}
                </>
              )}
            </div>
          </aside>
        </div>

        {/* Edit Dashboard Modal */}
        {isEditModalOpen && (
          <div className="premium-modal-overlay">
            <div className="premium-modal-box">
              <div className="modal-header">
                <h3>Edit Dashboard</h3>
                <button className="close-x" onClick={() => setIsEditModalOpen(false)}><FiX /></button>
              </div>
              <form onSubmit={handleEditSubmit}>
                <div className="modal-body">
                  <div className="form-group">
                    <label>Dashboard Name</label>
                    <input
                      type="text"
                      value={editDashName}
                      onChange={(e) => setEditDashName(e.target.value)}
                      placeholder="Enter dashboard name"
                      autoFocus
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn-cancel" onClick={() => setIsEditModalOpen(false)}>Close</button>
                  <button type="submit" className="btn-submit" disabled={loading}>
                    {loading ? 'Saving...' : 'Submit'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Create Dashboard Modal */}
        {isCreateModalOpen && (
          <div className="premium-modal-overlay">
            <div className="premium-modal-box">
              <div className="modal-header">
                <h3>Create Dashboard</h3>
                <button className="close-x" onClick={() => setIsCreateModalOpen(false)}><FiX /></button>
              </div>
              <form onSubmit={handleCreateSubmit}>
                <div className="modal-body">
                  <div className="form-group">
                    <label>Dashboard Name</label>
                    <input
                      type="text"
                      value={newDashName}
                      onChange={(e) => setNewDashName(e.target.value)}
                      placeholder="Enter Dashboard Name"
                      autoFocus
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn-cancel" onClick={() => setIsCreateModalOpen(false)}>Close</button>
                  <button type="submit" className="btn-submit intense" disabled={loading}>
                    {loading ? 'Processing...' : 'Continue'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Close Confirmation Alert */}
        <Alert
          show={showCloseAlert}
          type="confirm"
          title="Attention!"
          message="If you proceed with this action, you will lose all unsaved changes. Would you like to proceed?"
          confirmText="OK"
          cancelText="Cancel"
          onConfirm={handleConfirmClose}
          onCancel={handleCancelClose}
        />
      </div>
    </div>
  );
};

export default CustomDashboard;

const WidgetContent = ({ widget }) => {
  const [activePieIndex, setActivePieIndex] = useState(-1);
  const navigate = useNavigate();
  const type = (widget.ReportType || '').toLowerCase();
  const name = (widget.ReportName || '').trim();
  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];

  const dummyListData = [
    { name: 'Mr. Bansal', score: '0.05', id: '137749' },
    { name: 'Rohit', score: '0.05', id: '117758' },
    { name: 'Arun', score: '0.05', id: '122715' },
    { name: 'Rina', score: '0.05', id: '118583' },
    { name: 'Suman', score: '0.05', id: '124080' }
  ];

  const dummyPieData = [
    { name: 'Australia', value: 42.9 }, { name: 'Codeffective', value: 10.8 },
    { name: 'Online enquiry', value: 10.2 }, { name: 'Super Automation', value: 8.2 },
    { name: 'CAR DEKHO', value: 7.9 }, { name: 'free whatsapp', value: 6.6 },
    { name: 'Walkins', value: 4.1 }, { name: 'UAE', value: 3.8 }
  ];

  if (type.includes('counter')) {
    // Simplified: Pull backend-calculated Value directly
    // Fallback to random placeholder if no backend value yet
    const displayValue = widget.Value ?? widget.value ?? widget.Count ?? widget.count ?? widget.PlaceholderValue ?? 0;

    return (
      <div 
        className="counter-body-premium"
        onClick={() => {
          const section = (widget.SectionName || '').toLowerCase();
          const filter = name.replace(/\s+/g, '');
          if (section === 'leads' || section === 'lead' || filter.toLowerCase() === 'notfollowedupyet' || filter.toLowerCase() === 'notfollowup') {
            const isNotFollowUp = filter.toLowerCase() === 'notfollowedupyet' || filter.toLowerCase() === 'notfollowup' ? 1 : 0;
            const payloadState = { isRedirect: true, notFollowUp: isNotFollowUp, filterText: "" };
            navigate('/leads', { state: payloadState });
          }
        }}
        style={{ cursor: 'pointer' }}
      >
        <div className="counter-value">{displayValue}</div>
        <div className="counter-label">{name}</div>
      </div>
    );
  }

  if (type.includes('chart')) {
    let chartData = (Array.isArray(widget.Details) && widget.Details.length > 0) ? widget.Details.map(d => ({
      name: d.Name || d.ReportName || 'Other',
      value: Number(d.Value || d.TotalLeads || d.TotalCount || 0)
    })) : dummyPieData;

    return (
      <div className="chart-body-premium">
        <div style={{ width: '100%', height: 350 }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={90}
                dataKey="value"
                paddingAngle={2}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                labelLine={true}
                activeIndex={activePieIndex}
                activeShape={(props) => {
                  const RADIAN = Math.PI / 180;
                  const { cx, cy, midAngle, innerRadius, outerRadius, startAngle, endAngle, fill } = props;
                  const sin = Math.sin(-RADIAN * midAngle);
                  const cos = Math.cos(-RADIAN * midAngle);
                  const sx = cx + (outerRadius + 5) * cos;
                  const sy = cy + (outerRadius + 5) * sin;
                  return (
                    <g>
                      <Sector cx={cx} cy={cy} innerRadius={innerRadius} outerRadius={outerRadius + 5} startAngle={startAngle} endAngle={endAngle} fill={fill} />
                    </g>
                  );
                }}
                onMouseEnter={(_, index) => setActivePieIndex(index)}
                onMouseLeave={() => setActivePieIndex(-1)}
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="chart-footer-label" style={{ textAlign: 'center', marginTop: '10px' }}>
          <strong>{name}</strong>
          <p style={{ margin: '5px 0 0', fontSize: '11px', color: '#94a3b8' }}>Source Wise Enquiry Distribution</p>
        </div>
      </div>
    );
  }

  const listData = (Array.isArray(widget.Details) && widget.Details.length > 0) ? widget.Details : dummyListData;

  return (
    <div className="list-body-premium">
      <label className="list-title-sub">Top 10 leads with highest score</label>
      <div className="list-items" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {listData.slice(0, 10).map((item, i) => (
          <div key={i} className="list-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: i === listData.length - 1 ? 'none' : '1px solid #f1f5f9', paddingBottom: '8px', cursor: 'pointer' }}
            onClick={() => {
              const trueId = item.id || item.LeadId || item.LeadNo || item.ID;
              if (trueId) {
                let filterVal = `ID = '${trueId}'`;
                if (!item.id && !item.LeadId && item.LeadNo) {
                  filterVal = `LeadNo = '${item.LeadNo}'`;
                }
                const isNotFollowUp = (widget.ReportName || '').replace(/\s+/g, '').toLowerCase() === 'notfollowedupyet' || (widget.ReportName || '').replace(/\s+/g, '').toLowerCase() === 'notfollowup' ? 1 : 0;
                const payloadState = { 
                  isRedirect: true, 
                  notFollowUp: isNotFollowUp, 
                  filterText: filterVal 
                };
                navigate('/leads', { state: payloadState });
              }
            }}
          >
            <div className="row-left" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <img
                src={`https://ui-avatars.com/api/?name=${encodeURIComponent(item.PersonName || item.LeadName || item.Name || item.name || item.FirstName || 'User')}&background=random&color=fff`}
                alt="avatar"
                style={{ width: '36px', height: '36px', borderRadius: '50%' }}
              />
              <div className="info" style={{ display: 'flex', flexDirection: 'column' }}>
                <span className="name" style={{ fontSize: '0.85rem', fontWeight: '700', color: '#1e293b' }}>
                  {item.PersonName || item.LeadName || item.Name || item.name || item.FirstName || 'User'}
                </span>
                <span className="id" style={{ fontSize: '0.7rem', color: '#94a3b8' }}>
                  Lead No. {item.LeadNo || item.ID || item.Id || item.id || 'N/A'}
                </span>
              </div>
            </div>
            <span className="score" style={{ fontSize: '0.8rem', fontWeight: '600', color: '#10b981' }}>
              {item.TotalScore !== undefined ? item.TotalScore : (item.score !== undefined ? item.score : (item.Value !== undefined ? item.Value : '0.00'))}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};
